import React, { useEffect, useRef, useState, memo } from "react";
import { Puff } from "react-loader-spinner";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchMoreMessages, setActingMessage } from "redux/chats/chats.actions";
import AudioMessage from "./components/messages/AudioMessage";
import FileMessage from "./components/messages/FileMessage";
import ImageMessage from "./components/messages/ImageMessage";
import TextMessage from "./components/messages/TextMessage";
import VideoMessage from "./components/messages/VideoMessage";
import useTranslation from "hooks/useTranslation";
import {
  ReplyIcon,
  OneCheckIconComponent,
  TwoCheckIconComponent,
  IconCopyComponent,
  PencilIconComponent,
  ClockOutlineComponent,
} from "assets/icons";
import Menu from "./components/Menu";
import useCopyToClipboard from "hooks/useCopyToClipboard";
import moment from "jalali-moment";
import { toast } from "react-toastify";
import UserChannels from "pages/conversation/components/UserChannels";
import { getTimeKey } from "utils/date";
import chatBotAvatar from "assets/avatars/bot_avatar.svg";

const getSelectedConversation = ({ chatsData }) =>
  chatsData.selectedConversation;

const getScrollToBottom = ({ chatsData }) => chatsData.scrollToBottom;
const getIsLoadingChats = ({ chatsData }) => chatsData.isLoadingChats;
const getActingMessage = ({ chatsData }) => chatsData.messageUnderAction;

const ConversationsBody = () => {
  const [messagesSkip, setMessagesSkip] = useState(30);
  const [textToCopy, setTextToCopy] = useState("");
  const [sortedMessagesObj, setSortedMessagesObj] = useState();
  const prevChatMsgCount = useRef();

  const selectedConversation = useSelector(getSelectedConversation);
  const scrollToBottom = useSelector(getScrollToBottom);
  const isLoadingChats = useSelector(getIsLoadingChats);
  const actingMessage = useSelector(getActingMessage);

  const dispatch = useDispatch();
  const { getTranslation } = useTranslation(["CHAT", "COMMON"]);
  const { t } = getTranslation();
  const copyToClipBoard = useCopyToClipboard(textToCopy);
  const { convId } = useParams();

  // TODO: create component for time and date collection
  const renderTimeAndDateText = (text) => {
    const [date, time] = text.split(" ");

    const renderedTime = time ? time + " " : "";

    return (
      <>
        <span className="conv__body__body__inner__msgright__inner__bubble__text__info__date">
          {renderedTime}
        </span>

        <span>{date}</span>
      </>
    );
  };

  const showOptions =
    selectedConversation?.conversation?.convType === "ongoing";

  const OPERATORS_MSG_ACTIONS = [
    {
      id: "copy",
      text: (
        <div className="flex flex-ai-c">
          <div className="flex flex-ai-c">
            <IconCopyComponent />
          </div>
          <span>{t("COPY_TEXT")}</span>
        </div>
      ),
      handler: (msg) => {
        setTextToCopy(msg.content);
      },
    },
    {
      id: "edit",
      text: (
        <div className="flex flex-ai-c">
          <div className="flex flex-ai-c">
            <PencilIconComponent />
          </div>

          <span>{t("EDIT")}</span>
        </div>
      ),
      handler: (msg) => {
        if (msg.edited) {
          toast.warn(t("MESSAGE_HAS_BEEN_EDITED"));
          return;
        }

        if (!moment(msg.createdAt).isAfter(moment().subtract(12, "hours"))) {
          toast.warn(t("EDIT_ONLY_LAST_12_HOURS_MESSAGES"));
          return;
        }

        dispatch(
          setActingMessage({
            id: msg.id,
            content: msg.content,
            actionType: "EDIT",
          })
        );
      },
    },
  ];

  const USER_MSG_ACTIONS = [
    {
      text: (
        <div className="flex flex-ai-c">
          <div className="flex flex-ai-c">
            <IconCopyComponent />
          </div>
          <span>{t("COPY_TEXT")}</span>
        </div>
      ),
      handler: (msg) => {
        setTextToCopy(msg.content);
      },
    },
  ];

  const getTextMessage = (message) => {
    let urlRegex =
      /(https?:\/\/(?:www\.|(?!www)|http)[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
    let el = message.content.split(urlRegex);
    let links = [];
    return (
      <div>
        <>
          {el.map((item, index) => {
            if (item.match(urlRegex)) {
              links.push(item);
              return (
                <a rel="noreferrer" target="_blank" href={item}>
                  {item}
                </a>
              );
            } else {
              return item;
            }
          })}
        </>
      </div>
    );
  };

  const handleClickReply = (msg) => () => {
    const dataObj = {
      id: msg.id,
      content: "",
      actionType: "REPLY",
    };

    if (msg.type === "text" || msg.type === "emoji")
      dataObj["content"] = msg.content;
    else if (msg.type === "image")
      dataObj["content"] = t("SPECIAL_MSG_TYPES").IMAGE;
    else if (msg.type === "audio")
      dataObj["content"] = t("SPECIAL_MSG_TYPES").AUDIO;
    else if (msg.type === "file" || msg.type === "compress")
      dataObj["content"] = t("SPECIAL_MSG_TYPES").FILE;

    dispatch(setActingMessage(dataObj));
  };

  const getProperMessage = (message, prevMsg, nextMsg) => {
    // is prev message from operator
    const isPrevMsgFromAgent = !!prevMsg?.me;
    // is next message form operator
    const isNextMsgFromAgent = !!nextMsg?.me;

    const currentMsgAuthorId = message.author?.id;
    const prevMsgAuthorId = prevMsg?.author?.id;

    const prevAndCurrentAuthorIdDiffer = currentMsgAuthorId !== prevMsgAuthorId;

    let messageElement;
    switch (message.type) {
      case "text":
        messageElement = (
          <TextMessage
            content={getTextMessage(message)}
            delivered={message.delivered}
            time={message.time}
            replyId={message.replyId}
            id={message.id}
            status={message.status}
          />
        );
        break;
      case "emoji":
        messageElement = (
          <TextMessage
            content={message.content}
            delivered={message.delivered}
            time={message.time}
            replyId={message.replyId}
            id={message.id}
            status={message.status}
          />
        );
        break;
      case "image":
        messageElement = (
          <ImageMessage
            time={message.time}
            url={message.url}
            delivered={message.delivered}
            replyId={message.replyId}
            id={message.id}
            status={message.status}
          />
        );
        break;
      case "audio":
        messageElement = (
          <AudioMessage
            time={message.time}
            url={message.url}
            delivered={message.delivered}
            replyId={message.replyId}
            id={message.id}
            status={message.status}
            progress={message.progress}
          />
        );
        break;
      case "file":
        messageElement = (
          <FileMessage
            time={message.time}
            url={message.url}
            delivered={message.delivered}
            name={message.name}
            size={message.size}
            type={message.type}
            replyId={message.replyId}
            id={message.id}
            status={message.status}
            progress={message.progress}
          />
        );
        break;
      case "compress":
        messageElement = (
          <FileMessage
            time={message.time}
            url={message.url}
            delivered={message.delivered}
            name={message.name}
            size={message.size}
            type={message.type}
            replyId={message.replyId}
            id={message.id}
            status={message.status}
            progress={message.progress}
          />
        );
        break;
      case "video":
        messageElement = (
          <VideoMessage
            time={message.time}
            url={message.url}
            delivered={message.delivered}
            replyId={message.replyId}
            id={message.id}
            status={message.status}
            progress={message.progress}
          />
        );
        break;
      default:
        break;
    }

    if (message.me) {
      const showAgentAvatar =
        !isPrevMsgFromAgent || prevAndCurrentAuthorIdDiffer;

      return (
        <div className="flex flex-column">
          <div
            key={message.id}
            className="conv__body__body__inner__msgright__inner flex flex-ai-e flex-jc-s"
          >
            <div
              data-tool-tip-top={message.author?.name}
              className="conv__body__body__inner__msgright__inner__avatar"
            >
              {showAgentAvatar && (
                <img
                  src={message.isBot ? chatBotAvatar : message.author?.avatar}
                  alt=""
                />
              )}
            </div>
            <div className="flex flex-ai-c">
              {messageElement}
              {/* just show actions menu when conversation is accepted */}
              {/* actions menu */}
              {showOptions &&
                (message.type === "text" || message.type === "emoji") && (
                  <Menu
                    options={OPERATORS_MSG_ACTIONS}
                    side={"right"}
                    {...{ message }}
                  />
                )}
            </div>
          </div>
          {/* just show actions when conversation is accepted */}
          {
            <div className="conv__body__body__inner__msgright__info">
              {showOptions && (
                <div
                  onClick={handleClickReply(message)}
                  className="conv__body__body__inner__msgright__info__reply flex"
                >
                  <span>{t("REPLAY")}</span>
                  <img src={ReplyIcon} alt="reply" />
                </div>
              )}

              {message.edited && (
                <div className="conv__body__body__inner__msgright__info__eddited flex no-margin">
                  {t("EDITED")}
                </div>
              )}

              <div className="conv__body__body__inner__msgright__inner__bubble__text__info">
                <div className="time_info">
                  <div className="conv__body__body__inner__msgright__inner__bubble__text__info__time">
                    {renderTimeAndDateText(message.time)}
                  </div>
                  {!message.delivered ? (
                    <ClockOutlineComponent className="pendingClockIcon" />
                  ) : message.status === "sent" ? (
                    <OneCheckIconComponent className="OneCheckIcon" />
                  ) : message.status === "seen" ? (
                    <TwoCheckIconComponent className="TwoCheckIcon" />
                  ) : null}
                </div>
                <div className="flex flex-ai-c"></div>
              </div>
            </div>
          }
        </div>
      );
    } else {
      return (
        <div
          // TODO: could do better here and use classes for thses dynamic margins
          style={{
            marginTop: isPrevMsgFromAgent ? "20px" : "0px",
            marginBottom: isNextMsgFromAgent ? "20px" : "0px",
          }}
          className="flex flex-column"
        >
          <div
            key={message.id}
            className="conv__body__body__inner__msgleft__inner "
          >
            <div className="conv__body__body__inner__msgleft__inner__bubble flex flex-ai-e flex-jc-sb">
              <div className="flex flex-ai-c">
                {messageElement}
                {showOptions &&
                  (message.type === "text" || message.type === "emoji") && (
                    <Menu
                      options={USER_MSG_ACTIONS}
                      side={"left"}
                      {...{ message }}
                    />
                  )}
              </div>
            </div>
          </div>
          {/* just show actions when conversation is accepted */}

          <div className="conv__body__body__inner__msgleft__info">
            <div className="time_info">
              <div className="conv__body__body__inner__msgright__inner__bubble__text__info__time">
                {renderTimeAndDateText(message.time)}
              </div>
              {message.status === "sent" ? (
                <OneCheckIconComponent className="OneCheckIcon" />
              ) : message.status === "seen" ? (
                <TwoCheckIconComponent className="TwoCheckIcon" />
              ) : null}
            </div>
            {showOptions && (
              <>
                <div
                  onClick={handleClickReply(message)}
                  className="conv__body__body__inner__msgleft__info__reply flex"
                >
                  <span>{t("REPLAY")}</span>
                  <img src={ReplyIcon} alt="reply" />
                </div>
                {message.edited && (
                  <div className="conv__body__body__inner__msgleft__info__eddited flex">
                    {t("EDITED")}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }
  };

  const convBodyRef = useRef(null);

  const handleScroll = (e) => {
    const element = e.target;

    if (
      element.scrollTop === 0 &&
      element.clientHeight < element.scrollHeight &&
      selectedConversation
    ) {
      dispatch(
        fetchMoreMessages({
          conversationId: selectedConversation.conversation.id,
          skip: messagesSkip,
        })
      );
      setMessagesSkip(messagesSkip + 30);
    }

    //  body.scrollTop = body.scrollHeight;
    const scrollDownBtn = document.querySelector("#scroll-down");
    if (!scrollDownBtn) return;

    if (element.scrollTop < element.scrollHeight - 1000) {
      scrollDownBtn.classList.replace("display-none", "flex");
    } else {
      scrollDownBtn.classList.replace("flex", "display-none");
    }
  };

  useEffect(() => {
    const msgNumChanged =
      prevChatMsgCount.current !==
      selectedConversation?.conversation.chats?.length;

    if (scrollToBottom && !!sortedMessagesObj && msgNumChanged) {
      let body = convBodyRef.current;
      body.scrollTop = body.scrollHeight;
    }

    prevChatMsgCount.current =
      selectedConversation?.conversation?.chats?.length;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMessagesObj]);

  useEffect(() => {
    if (textToCopy) {
      copyToClipBoard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textToCopy]);

  const renderChatLoader = () => {
    if (!selectedConversation && !isLoadingChats && !convId)
      return (
        <div className="flex flex-ai-c flex-jc-c h-100">
          <UserChannels />
        </div>
      );

    return (
      <div className="flex flex-ai-c flex-jc-c h-100">
        <Puff
          height="60"
          width="60"
          radius="9"
          color="#b10e84"
          ariaLabel="three-dots-loading"
        />
      </div>
    );
  };

  useEffect(() => {
    dispatch(setActingMessage(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  function getSortedMessages(messages) {
    const sortedMessages = {};

    messages.forEach((message, idx) => {
      const messageTime = message?.createdAt?.split("T")?.[0];

      if (!!sortedMessages[messageTime]) {
        sortedMessages[messageTime].push(message);
      } else {
        sortedMessages[messageTime] = [message];
      }
    });

    return sortedMessages;
  }

  useEffect(() => {
    if (!!selectedConversation?.conversation?.chats) {
      const sorted_messages = getSortedMessages(
        selectedConversation.conversation.chats
      );

      setSortedMessagesObj(sorted_messages);
    }
  }, [selectedConversation, selectedConversation?.conversation?.chats]);

  return (
    <div
      onScroll={handleScroll}
      ref={convBodyRef}
      id="convBodyEl"
      className="conv__body__body h-100"
      data-show-preview={!!actingMessage}
    >
      <div className="conv__body__body__inner h-100 ">
        <div className="conv__body__body__inner__msgright h-100 ">
          {selectedConversation && !isLoadingChats && !!sortedMessagesObj
            ? Object.keys(sortedMessagesObj).map((time_key, i) => {
                return (
                  <>
                    <div className="time_key">
                      <span className="time_key_seperator"></span>
                      {getTimeKey(time_key)}
                      {" ماه"}
                      <span className="time_key_seperator"></span>
                    </div>
                    {sortedMessagesObj[time_key].map((message, idx) => {
                      const msgIndex =
                        selectedConversation.conversation.chats.findIndex(
                          (msg) => msg.id === message.id
                        );

                      return getProperMessage(
                        message, // current msg
                        selectedConversation.conversation.chats[msgIndex - 1],
                        selectedConversation.conversation.chats[msgIndex + 1]
                      );
                    })}
                  </>
                );
              })
            : renderChatLoader()}
        </div>
      </div>
    </div>
  );
};

export default memo(ConversationsBody);

// clear acting message when changin conversation
// you can edit only messages that semt in last 12 hrs
